"use client";

import { useEffect, useState } from 'react';
import { Check, Copy, Linkedin, Mail, Share2, Smartphone, Twitter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button as DsButton } from '@/components/ds';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

type Variant = 'icon' | 'button';
type Size = 'sm' | 'md';

interface ShareJobButtonProps {
  // Job-specific shape (back-compat with /jobs callers).
  jobId?: string;
  jobTitle?: string;
  // Generic shape — used by profile share and other surfaces.
  path?: string;
  title?: string;
  /** Override the share subject/body label entirely (rare). */
  subjectPrefix?: string;
  variant?: Variant;
  size?: Size;
  className?: string;
  onShared?: (channel: 'copy' | 'email' | 'twitter' | 'linkedin' | 'native') => void;
}

function buildAbsoluteUrl(path: string): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }
  return path;
}

export default function ShareJobButton({
  jobId,
  jobTitle,
  path,
  title,
  subjectPrefix,
  variant = 'icon',
  size = 'sm',
  className,
  onShared,
}: ShareJobButtonProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const resolvedPath = path ?? (jobId ? `/jobs/${jobId}` : '/');
  const resolvedTitle = title ?? jobTitle ?? '';
  const url = buildAbsoluteUrl(resolvedPath);
  const isJob = !!jobId;
  const subject = subjectPrefix
    ? `${subjectPrefix}: ${resolvedTitle}`.trim()
    : isJob
    ? `Check out this job: ${resolvedTitle}`
    : resolvedTitle
    ? `Check this out: ${resolvedTitle}`
    : 'Check this out on Indiestry';
  const text = resolvedTitle
    ? `${resolvedTitle} on Indiestry`
    : 'Indiestry';

  const handleStop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCopy = async (e: React.MouseEvent) => {
    handleStop(e);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: 'Link copied', description: 'Link copied to clipboard.', variant: 'success' });
      onShared?.('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      toast({
        title: 'Could not copy link',
        description: 'Please copy the URL manually.',
        variant: 'destructive',
      });
    }
  };

  const openIntent = (intentUrl: string) => {
    if (typeof window !== 'undefined') {
      window.open(intentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEmail = (e: React.MouseEvent) => {
    handleStop(e);
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;
    if (typeof window !== 'undefined') window.location.href = mailto;
    onShared?.('email');
  };

  const handleTwitter = (e: React.MouseEvent) => {
    handleStop(e);
    openIntent(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    );
    onShared?.('twitter');
  };

  const handleLinkedIn = (e: React.MouseEvent) => {
    handleStop(e);
    openIntent(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
    onShared?.('linkedin');
  };

  const handleNative = async (e: React.MouseEvent) => {
    handleStop(e);
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return;
    try {
      await navigator.share({ title: subject, text, url });
      onShared?.('native');
    } catch (err) {
      // User canceled or device doesn't permit — silent.
      if ((err as DOMException)?.name !== 'AbortError') {
        console.error('Native share failed:', err);
      }
    }
  };

  const ariaLabel = isJob ? 'Share job' : 'Share';

  const trigger =
    variant === 'icon' ? (
      <DsButton
        variant="ghost"
        size={size}
        aria-label={ariaLabel}
        className={className}
        onClick={handleStop}
      >
        <Share2 className="w-4 h-4" strokeWidth={1.75} />
      </DsButton>
    ) : (
      <DsButton
        variant="secondary"
        size={size}
        aria-label={ariaLabel}
        className={className}
        onClick={handleStop}
      >
        <Share2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        Share
      </DsButton>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-surface-1 border-border-subtle text-text-primary min-w-[200px]"
        onClick={handleStop}
      >
        <DropdownMenuItem
          className={cn('cursor-pointer hover:bg-surface-2', copied && 'text-accent')}
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="w-4 h-4 mr-2" strokeWidth={1.75} />
          ) : (
            <Copy className="w-4 h-4 mr-2" strokeWidth={1.75} />
          )}
          {copied ? 'Link copied' : 'Copy link'}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer hover:bg-surface-2" onClick={handleEmail}>
          <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Email
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer hover:bg-surface-2" onClick={handleTwitter}>
          <Twitter className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Share on X (Twitter)
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer hover:bg-surface-2" onClick={handleLinkedIn}>
          <Linkedin className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Share on LinkedIn
        </DropdownMenuItem>
        {canNativeShare && (
          <>
            <DropdownMenuSeparator className="bg-border-subtle" />
            <DropdownMenuItem className="cursor-pointer hover:bg-surface-2" onClick={handleNative}>
              <Smartphone className="w-4 h-4 mr-2" strokeWidth={1.75} />
              More options…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
